import { alpha, createTheme } from '@mui/material/styles';

// Retention-only design tokens. Applied by RetentionThemeProvider around the /student-journey/* routes,
// layered on the app theme (so fonts, customShadows and the global table keep working).

export const RT = {
  primary: '#4F46E5',
  primaryDark: '#3730A3',
  primaryLight: '#EEF0FF',
  ink: '#0F172A',
  ink2: '#334155',
  muted: '#64748B',
  faint: '#94A3B8',
  line: '#E6E9F2',
  surface: '#F6F7FB',
  paper: '#FFFFFF',
  success: '#16A34A',
  warning: '#F59E0B',
  error: '#E11D48',
  info: '#0284C7',
  whatsapp: '#16A34A',
  radius: 14,
  shadow: '0 1px 2px rgba(15,23,42,.04), 0 8px 24px -12px rgba(15,23,42,.12)',
  shadowHover: '0 2px 4px rgba(15,23,42,.06), 0 16px 32px -16px rgba(79,70,229,.30)',
};

// Phase colours, used by the stepper, stage cards and chips.
export const PHASE_TOKENS = {
  1: { main: '#2563EB', light: '#EFF4FF', soft: '#DBE6FE', gradient: 'linear-gradient(135deg,#2563EB 0%,#60A5FA 100%)' },
  2: { main: '#7C3AED', light: '#F4EFFF', soft: '#E6DAFE', gradient: 'linear-gradient(135deg,#7C3AED 0%,#C084FC 100%)' },
  3: { main: '#059669', light: '#E8F8F1', soft: '#C9F0DF', gradient: 'linear-gradient(135deg,#059669 0%,#34D399 100%)' },
};

export const HERO_GRADIENT = 'linear-gradient(120deg,#312E81 0%,#4F46E5 55%,#7C3AED 100%)';

const retentionOverrides = {
  palette: {
    primary: { main: RT.primary, dark: RT.primaryDark, light: '#818CF8', lighter: RT.primaryLight, contrastText: '#fff' },
    secondary: { main: '#7C3AED', dark: '#6D28D9', light: '#A78BFA', contrastText: '#fff' },
    success: { main: RT.success, dark: '#15803D', light: '#4ADE80', lighter: '#E8F7EE', contrastText: '#fff' },
    warning: { main: RT.warning, dark: '#D97706', light: '#FCD34D', lighter: '#FEF6E4', contrastText: '#fff' },
    error: { main: RT.error, dark: '#BE123C', light: '#FB7185', lighter: '#FDECF0', contrastText: '#fff' },
    info: { main: RT.info, dark: '#0369A1', light: '#38BDF8', lighter: '#E6F4FB', contrastText: '#fff' },
    text: { primary: RT.ink, secondary: RT.muted, disabled: RT.faint },
    divider: RT.line,
    background: { default: RT.surface, paper: RT.paper },
  },
  shape: { borderRadius: 10 },
  typography: {
    h4: { fontSize: 24, fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.25 },
    h5: { fontSize: 20, fontWeight: 800, letterSpacing: '-0.015em', lineHeight: 1.3 },
    h6: { fontSize: 16, fontWeight: 700, letterSpacing: '-0.01em', lineHeight: 1.4 },
    subtitle1: { fontSize: 14.5, fontWeight: 700 },
    subtitle2: { fontSize: 13, fontWeight: 700 },
    body1: { fontSize: 14 },
    body2: { fontSize: 13 },
    caption: { fontSize: 12 },
    overline: { fontSize: 11, fontWeight: 800, letterSpacing: '0.08em' },
    button: { fontSize: 13.5, fontWeight: 700, textTransform: 'none' },
  },
  components: {
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: { borderRadius: RT.radius + 2, border: `1px solid ${RT.line}`, boxShadow: RT.shadow, backgroundImage: 'none' },
      },
    },
    MuiCardContent: { styleOverrides: { root: { padding: 20, '&:last-child': { paddingBottom: 20 } } } },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: { borderRadius: 10, paddingInline: 16, minHeight: 36, whiteSpace: 'nowrap' },
        sizeSmall: { minHeight: 30, paddingInline: 12, fontSize: 12.5, borderRadius: 8 },
        sizeLarge: { minHeight: 44, paddingInline: 22, fontSize: 14.5 },
        contained: { boxShadow: `0 6px 14px -8px ${alpha(RT.primary, 0.7)}`, '&:hover': { boxShadow: `0 8px 18px -8px ${alpha(RT.primary, 0.8)}` } },
        containedSuccess: { boxShadow: `0 6px 14px -8px ${alpha(RT.success, 0.7)}` },
        containedError: { boxShadow: `0 6px 14px -8px ${alpha(RT.error, 0.7)}` },
        outlined: { borderColor: RT.line, color: RT.ink2, backgroundColor: RT.paper, '&:hover': { borderColor: RT.primary, backgroundColor: RT.primaryLight, color: RT.primary } },
        text: { '&:hover': { backgroundColor: RT.primaryLight } },
      },
    },
    MuiIconButton: {
      styleOverrides: { root: { borderRadius: 10 } },
    },
    MuiChip: {
      styleOverrides: {
        root: { borderRadius: 8, fontWeight: 700, fontSize: 12 },
        sizeSmall: { height: 24 },
        label: { paddingInline: 10 },
        outlined: { borderColor: RT.line, backgroundColor: RT.paper },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: { minHeight: 44 },
        indicator: { display: 'none' },
        flexContainer: { gap: 6 },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          minHeight: 40,
          borderRadius: 10,
          padding: '8px 14px',
          textTransform: 'none',
          fontSize: 13.5,
          fontWeight: 700,
          color: RT.muted,
          '&:hover': { color: RT.ink, backgroundColor: alpha(RT.primary, 0.06) },
          '&.Mui-selected': { color: '#fff', backgroundColor: RT.primary, boxShadow: `0 6px 14px -8px ${alpha(RT.primary, 0.8)}` },
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: 12, fontSize: 13, alignItems: 'center' },
        standardInfo: { backgroundColor: RT.primaryLight, color: RT.primaryDark },
      },
    },
    MuiToggleButtonGroup: {
      styleOverrides: {
        root: { backgroundColor: '#EEF0F6', borderRadius: 12, padding: 3, gap: 3 },
        grouped: { border: 0, borderRadius: '9px !important' },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 700,
          fontSize: 12.5,
          color: RT.muted,
          padding: '6px 14px',
          '&.Mui-selected': { backgroundColor: RT.paper, color: RT.primary, boxShadow: '0 1px 3px rgba(15,23,42,.12)' },
          '&.Mui-selected:hover': { backgroundColor: RT.paper },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: { tooltip: { backgroundColor: RT.ink, fontSize: 12, borderRadius: 8, padding: '6px 10px' } },
    },
    MuiAvatar: { styleOverrides: { root: { fontWeight: 800 } } },
    MuiLinearProgress: {
      styleOverrides: { root: { borderRadius: 99, height: 8, backgroundColor: '#EEF0F6' }, bar: { borderRadius: 99 } },
    },
  },
};

// Nested theme: the app theme with the retention overrides deep-merged on top.
export const createRetentionTheme = (outerTheme) => createTheme(outerTheme, retentionOverrides);
